import type { InFlightBillet, StandRow } from "../../../models/TrendDisplay";
import type { VisualizerBillet } from "../models/MillVisualizer";

// The SP returns FirstStandPosition / LastStandPosition in CANONICAL 1..18
// space (matches MILL.BilletInStands.StandId, MILL.GetBilletInStandTagsId().StandId).
// The visualizer renders in FILTERED-VISIBLE-INDEX space (the array after
// `filterVisibleStands` drops S1 / StrtT / StrtB and any other hidden stands
// — those have `PhysicalPosition` either null or different from their array
// index). This module converts between the two.
//
// Algorithm:
//   - For `first`, find the FIRST visible stand whose PhysicalPosition >=
//     the SP-returned canonical position. (If no visible stand at or
//     after that position, drop the billet.)
//   - For `last`, find the LAST visible stand whose PhysicalPosition <=
//     the SP-returned canonical position. (Symmetric.)
//
// This clamp rule deliberately snaps INWARD when the SP's range edge falls
// on a hidden stand. The bar physically traverses the hidden stand; the
// rendered capsule just doesn't extend to a column that isn't drawn.

const toFilteredIndex = (
	visibleStands: StandRow[],
	physicalPosition: number,
	direction: "first" | "last",
): number | null => {
	if (visibleStands.length === 0) return null;
	if (direction === "first") {
		for (let i = 0; i < visibleStands.length; i++) {
			const p = visibleStands[i].PhysicalPosition;
			if (p != null && p >= physicalPosition) return i;
		}
		return null;
	}
	for (let i = visibleStands.length - 1; i >= 0; i--) {
		const p = visibleStands[i].PhysicalPosition;
		if (p != null && p <= physicalPosition) return i;
	}
	return null;
};

const computePositionRatio = (avgIndex: number, count: number): number => {
	if (count <= 1) return 0;
	return Math.max(0, Math.min(1, avgIndex / (count - 1)));
};

// Convert the SP's in-flight rows into VisualizerBillet[] in
// filtered-visible-index space (1-based positions). One capsule per row.
//
// Drop rows where the [first, last] range can't be located inside the
// visible-stands array (e.g., the entire range falls on hidden stands).
// Drop rows where first > last after filtering.
export const mapInFlightBillets = (
	visibleStands: StandRow[],
	inFlight: InFlightBillet[],
): VisualizerBillet[] => {
	const out: VisualizerBillet[] = [];
	for (const b of inFlight) {
		const firstIdx = toFilteredIndex(
			visibleStands,
			b.FirstStandPosition,
			"first",
		);
		const lastIdx = toFilteredIndex(visibleStands, b.LastStandPosition, "last");
		if (firstIdx == null || lastIdx == null || firstIdx > lastIdx) continue;
		const firstPos = firstIdx + 1; // 1-based for the SVG layout
		const lastPos = lastIdx + 1;
		out.push({
			id: `billet-${b.BilletId}`,
			billetId: b.BilletId,
			heatNumber: b.HeatNumber,
			firstStandPosition: firstPos,
			lastStandPosition: lastPos,
			positionRatio: computePositionRatio(
				(firstIdx + lastIdx) / 2,
				visibleStands.length,
			),
		});
	}
	return out.sort((a, b) => a.firstStandPosition - b.firstStandPosition);
};
